import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject, Observable } from 'rxjs';

import { CrossDomainStorageService } from './cross-domain-storage.service';
import { suppressIdleResetContext } from 'src/app/core/interceptor/background-request.context';
import { TOKEN_NAME } from 'src/app/shared/services/session.service';

const CHECK_INTERVAL = 1000 * 60; // in ms = 1 minute
const MIN_INTERVAL_SEND = 2000; // in ms = 2 seconds
const LOCK_KEY = 'last_action_lock';

// Mínimo entre dois writes de last_action a partir de eventos de presença
// (mouse/teclado/scroll), para não escrever no storage a cada pixel.
const PRESENCE_DEBOUNCE = 1000 * 30; // 30s
// Teto do intervalo de keepalive ao backend.
const KEEPALIVE_MAX_INTERVAL = 1000 * 60 * 5; // 5 min
const KEEPALIVE_URL = '/api/accounts/auth/keepalive/';

@Injectable({
  providedIn: 'root'
})
export class InactiveCheckService {
  static instance: InactiveCheckService;
  private started = false;
  private eventBus: Subject<number>;
  /** Emite quando o backend já expirou a sessão (keepalive 401). */
  private expiredBus: Subject<void>;
  private interval: any = null;
  private lockCount = 0;

  /** Última presença real (mouse/teclado/scroll), em memória, por aba. */
  private lastPresence = 0;
  /** Último write de last_action a partir de presença (debounce). */
  private lastPresenceWrite = 0;
  /** Último keepalive enviado ao backend. */
  private lastKeepAlive = 0;
  /** Intervalo de keepalive (definido em start, a partir do tmLogout). */
  private keepAliveInterval = 0;
  /**
   * Quando true, eventos de presença NÃO renovam a sessão. Ativado enquanto o
   * aviso de expiração está aberto: só um keepalive confirmado ("Continuar
   * conectado") ou atividade em outra aba devem renovar — assim um clique no
   * próprio modal não mascara uma falha de keepalive.
   */
  private trackingPaused = false;
  private presenceHandler = () => this.onUserPresence();

  constructor(
    private storageService: CrossDomainStorageService,
    private http: HttpClient
  ) {
    if (!InactiveCheckService.instance) {
      this.eventBus = new Subject<number>();
      this.expiredBus = new Subject<void>();
      InactiveCheckService.instance = this;
    }

    return InactiveCheckService.instance;
  }

  private run(): void {
    if (this.started) { return; }
    this.started = true;

    window.addEventListener('mousemove', this.presenceHandler, { passive: true });
    window.addEventListener('mousedown', this.presenceHandler, { passive: true });
    window.addEventListener('click', this.presenceHandler, { passive: true });
    window.addEventListener('scroll', this.presenceHandler, { passive: true });
    window.addEventListener('keydown', this.presenceHandler, { passive: true });

    window.addEventListener('beforeunload', (event) => {
      this.storageService.getItem<number>(LOCK_KEY).then(lock => {
        this.storageService.setItem(LOCK_KEY, lock - this.lockCount);
        this.lockCount = 0;
      });
    });

    this.check();
    this.interval = setInterval(() => this.check(), CHECK_INTERVAL);
  }

  /**
   * Presença real do usuário: registra atividade (com debounce no storage) e
   * mantém o backend vivo via keepalive enquanto houver presença recente.
   */
  private onUserPresence(): void {
    // Durante o aviso de expiração, presença não renova (exige keepalive
    // confirmado). Evita que o clique no próprio modal mascare uma falha.
    if (this.trackingPaused) { return; }

    const now = Date.now();
    this.lastPresence = now;

    if (now - this.lastPresenceWrite > PRESENCE_DEBOUNCE) {
      this.lastPresenceWrite = now;
      this.updateLastUserAction();
    }

    this.maybeKeepAlive(now);
  }

  /**
   * Envia keepalive ao backend quando há presença recente, no máximo uma vez
   * por `keepAliveInterval`. Mantém a sessão de um usuário presente que não
   * está gerando outras requisições (ex.: lendo/preenchendo formulário).
   */
  private maybeKeepAlive(now: number): void {
    if (this.keepAliveInterval <= 0) { return; }
    if (now - this.lastPresence > this.keepAliveInterval) { return; }
    if (now - this.lastKeepAlive < this.keepAliveInterval) { return; }

    // Throttle (evita spam em falha persistente). suppressIdleResetContext:
    // o interceptor NÃO reseta last_action no envio — só renovamos o timer
    // local no sucesso, para uma falha (5xx/rede) não mascarar a expiração.
    this.lastKeepAlive = now;
    const requestToken = this.storageService.getItemAsync(TOKEN_NAME);
    this.http
      .post(KEEPALIVE_URL, {}, { context: suppressIdleResetContext() })
      .subscribe({
        next: () => this.updateLastUserAction(),
        error: (err) => {
          // 401 = backend já expirou/deletou a sessão. Não deixar a presença
          // local mascarar isso: sinaliza para o caller deslogar.
          if (err?.status === 401 && !this.isResponseFromStaleSession(requestToken)) {
            this.expiredBus.next();
          }
        },
      });
  }

  private check() {
    this.storageService
      .getItem<number>(LOCK_KEY)
      .then(lock => +lock > 0 ? Promise.resolve(-1) : this.storageService.getItem<number>(this.storageService.LAST_ACTION_KEY))
      .then(last => {
        if (last !== -1) {
          const time = Date.now() - (!last ? Date.now() : last);

          if (time > MIN_INTERVAL_SEND) {
            this.eventBus.next(time);
          }
        }
      });
  }

  public updateLastUserAction(): void {
    if (this.started) {
      this.storageService.updateLastUserAction();
    }
  }

  public start(tmLogout = 0): void {
    // keepalive = metade do tempo de sessão, limitado a 5 min.
    this.keepAliveInterval = tmLogout > 0
      ? Math.min(tmLogout / 2, KEEPALIVE_MAX_INTERVAL)
      : 0;
    // Baseline de inatividade: garante last_action = agora ao iniciar (ex.:
    // logo após login). Sem isso, com last_action ausente o check() trata
    // como "agora" e o aviso/logout do frontend nunca dispararia para quem
    // loga e sai sem nenhuma atividade.
    if (!this.started) {
      this.lastPresence = Date.now();
      this.storageService.updateLastUserAction();
    }
    this.check();
    this.run();
  }

  /**
   * Renova a sessão imediatamente (ex.: ação "Continuar conectado").
   * O estado local só é resetado APÓS o backend confirmar (204) — senão a UI
   * pareceria renovada com a sessão já morta no servidor. Propaga o erro para
   * o caller decidir (401 → login; falha transitória → manter o aviso).
   */
  public keepAliveNow(): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      const requestToken = this.storageService.getItemAsync(TOKEN_NAME);
      // suppressIdleResetContext: o interceptor NÃO reseta last_action no
      // envio; só renovamos o timer local aqui, após o 204 confirmado.
      this.http
        .post(KEEPALIVE_URL, {}, { context: suppressIdleResetContext() })
        .subscribe({
          next: () => {
            this.lastKeepAlive = Date.now();
            this.lastPresence = Date.now();
            this.updateLastUserAction();
            observer.next(true);
            observer.complete();
          },
          error: (err) => {
            // O modal/requisição pode pertencer ao layout anterior. Um 401 do
            // token antigo, recebido após o relogin, não encerra a sessão nova.
            if (err?.status === 401 && this.isResponseFromStaleSession(requestToken)) {
              observer.complete();
              return;
            }
            observer.error(err);
          },
        });
    });
  }

  private isResponseFromStaleSession(requestToken: string): boolean {
    const currentToken = this.storageService.getItemAsync(TOKEN_NAME);
    // Token diferente = houve relogin. Token atual ausente = o interceptor já
    // encerrou esta sessão. Nos dois casos, o callback tardio não age de novo.
    return !!requestToken && requestToken !== currentToken;
  }

  public stop(): void {
    clearTimeout(this.interval);
    this.interval = null;
    this.started = false;
    this.lockCount = 0;
    window.removeEventListener('mousemove', this.presenceHandler);
    window.removeEventListener('mousedown', this.presenceHandler);
    window.removeEventListener('click', this.presenceHandler);
    window.removeEventListener('scroll', this.presenceHandler);
    window.removeEventListener('keydown', this.presenceHandler);
  }

  public reset(): void {
    this.stop();
    // O reset separa integralmente duas sessões. Em especial, se a sessão
    // expirou com o modal de aviso aberto, trackingPaused não pode vazar para
    // o próximo login e impedir o registro de presença/keepalive.
    this.trackingPaused = false;
    this.lastPresence = 0;
    this.lastPresenceWrite = 0;
    this.lastKeepAlive = 0;
    this.keepAliveInterval = 0;
    this.storageService.removeItem(this.storageService.LAST_ACTION_KEY);
    this.storageService.removeItem(LOCK_KEY);
  }

  public event(): Observable<any> {
    return this.eventBus.asObservable();
  }

  /** Emite quando o backend já expirou a sessão (keepalive 401). */
  public expired(): Observable<void> {
    return this.expiredBus.asObservable();
  }

  /** Pausa a renovação por presença (enquanto o aviso de expiração está aberto). */
  public pauseTracking(): void {
    this.trackingPaused = true;
  }

  /** Retoma a renovação por presença. */
  public resumeTracking(): void {
    this.trackingPaused = false;
  }

  /**
   * Lê a inatividade atual do storage compartilhado (cross-tab), de forma
   * pontual. Usado pelo countdown do aviso para não deslogar se outra aba
   * registrou atividade após o aviso abrir. Respeita o LOCK_KEY.
   */
  public peekInactivity(): Promise<number> {
    return this.storageService.getItem<number>(LOCK_KEY).then((lock) => {
      if (+lock > 0) {
        return 0;
      }
      return this.storageService
        .getItem<number>(this.storageService.LAST_ACTION_KEY)
        .then((last) => Date.now() - (!last ? Date.now() : last));
    });
  }

  public lock(): void {
    this.storageService.getItem<number>(LOCK_KEY).then(lock => {
      this.lockCount++;
      this.storageService.setItem(LOCK_KEY, (lock || 0) + 1);
    });
  }

  public unlock(): void {
    this.storageService.getItem<number>(LOCK_KEY).then(lock => {
      this.lockCount--;
      this.storageService.setItem(LOCK_KEY, --lock);
    });
  }
}
